import { ICatalog } from "./Catalog";

export interface IGetOrganizationsResponse {
    organizations: IOrganization[];
}

export interface IGetOrganizationRefsResponse {
    organizations: IOrganizationRef[];
}

export interface IOrganizationRef{
    id: string;
    name: string;
}

export interface IOrganization {
    id: string;
    name: string;
    logo?: string;
    defaultCatalog?: string;
    country?: string;
    catalogs: ICatalog[];
    users?: IUserRoleRecord[];
    punchoutCurrency?: string;
}

export interface IOrganizationEntitlements {
    storefrontTier: string;
    storefrontLimit: number;
    storefrontProductLimit: number;
    storefrontSavedSearchLimit: number;
    storefrontTransactionLimit: number;
    storefrontAttributeLimit: number;
    storefrontDomains: string[];
    /* Self-serve custom domain provisioning (AC-1072) — gates the add-domain flow only. */
    customDomainsEnabled?: boolean;
    customDomainsLimit?: number;
    adHocEnabled?: boolean;
    userLimit: number;
    catalogLimit: number;
    licensedUserCount?: number;
    searchCredits?: number;
    subOrganizationsEnabled: boolean;
    /* Per-customer (sub-organization) vendor price files. Effective value from the API: it is only
       true when sub-organizations are enabled as well. */
    subOrganizationPriceFilesEnabled?: boolean;
    productLevel: string;
    apiUsageBillingEnabled: boolean;
    llmEnabled: boolean;
    customerRequestAnalyzerEnabled?: boolean;
    documentAnalyzerEnabled?: boolean;
    procurementEnabled: boolean;
    hidePriceFilter?: boolean;
    smtpEnabled?: boolean;
    productRecommendationsEnabled?: boolean;
    /* Gates the storefront search-bar autocomplete popout and its per-storefront toggles. */
    searchAutocompleteEnabled?: boolean;
    aiRestyleEnabled?: boolean;
    aiRestyleDailyRequestLimit?: number;
    aiBundleSuggestionsEnabled?: boolean;
    /* Gates the internal /settings/reference tool (the deep-link/settings-audit page). Adaptive
       Catalog staff orgs only — never surfaced or explained to a tenant without it. */
    settingsReferenceEnabled?: boolean;
}

export interface IOrganizationFeatureSettings {
    excludeFromBestPrice?: string[];
    enableDynamicProductListings?: boolean;
    enableSimpleTaxCalculation?: boolean;
    dynamicProductListingUpdateSchedule?: string;
    /* Cron override for the nightly search-suggestion build. Null uses the low-usage default. */
    searchSuggestionsSchedule?: string;
    /** Months the product "Ordered" counters look back; 0 or unset counts all time. */
    purchaseStatsMonths?: number;
    /** Cron override for the nightly purchase-counter recalculation. */
    purchaseStatsSchedule?: string;
    id: string;
}

export interface IUpdateOrganizationRequest {
    id: string;
    name?: string;
    logo?: string;
    country?: string;
    defaultCatalog?: string;
    punchoutCurrency?: string;
}

export interface IOrganizationProcurementSettings{
    contactFirstName?: string;
    contactLastName?: string;
    contactEmail?: string;
    contactPhone?: string;
    address1?: string;
    address2?: string;
    city?: string;
    stateProvince?: string;
    postalCode?: string;
    id: string;
}

export const STOREFRONT_TIER_STARTER = 'Starter';
export const STOREFRONT_TIER_PROFESSIONAL = 'Professional';
export const STOREFRONT_TIER_ENTERPRISE = 'Enterprise';

export interface IUserRoleRecord {
    username: string;
    role: string;
    mfaMode?: string;
}

export interface IUserCreateRequest extends IUserRoleRecord {
    firstName?: string;
    lastName?: string;
}

export interface IDisplayedField {
    id: number;
    field: string;
    size: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12 | 13 | 14 | 15 | 16
}

export interface IConfiguration {
    name: string;
    description: string;
    protected: boolean;
    dataType: string;
    defaultValue: string;
}

export interface IGetPermissionsResponse {
    permissions: string[];
    allPermissions: {[key: string]: string}
}

export interface IColumns {
    searchColumns: string[];
    workspaceColumns: string[];
}

export interface ICustomColumnSet {
    columnType: string;
    id?: string;
    columns?: string[]
}

export interface ISubOrganization {
    id: string;
    parentOrganization: string;
    name: string;
    logo?: string;
    country?: string;
    procurementEnabled?: boolean;
    contact1?: string;
    contact2?: string;
    contactEmail?: string;
    contactPhone?: string;
    address1?: string;
    address2?: string;
    city?: string;
    stateProvince?: string;
    postalCode?: string;
}

export interface ICreateSubOrganizationRequest {
    name: string;
    logo?: string;
    country?: string;
    contact1?: string;
    contact2?: string;
    contactEmail?: string;
    contactPhone?: string;
    address1?: string;
    address2?: string;
    city?: string;
    stateProvince?: string;
    postalCode?: string;
}



export interface IUserSettings {
    keywordToTags?: boolean;
}

export interface IUserSettingsUpdateRequest {
    keywordToTags?: boolean;
}

export const CURRENCIES = [
    'USD',
    'CAD',
    'EUR',
    'GBP',
    'AUD',
    'NZD',
]

export interface IVendorSettings {
    sourceVendor: string;
    vendorDisplayName?: string;
    enabled?: boolean;
    punchoutVendorName?: string;
    /** Maps this vendor to an external CRM/PSA vendor per folio: folio source id -> external vendor id. */
    crmVendorMappings?: { [folioId: string]: string };
}

export interface IKnownVendor {
    sourceVendor: string;
    vendorDisplayName?: string;
}

export interface ISearchSettings {
    showFacetsInSearchResults?: boolean;
    numberOfFacets?: number;
    defaultInStock?: boolean;
    defaultMatchAllKeywords?: boolean;
    showDesiredQuantity?: boolean;
    enableExpandedSearch?: boolean;
    expandedSearchThreshold?: number;
}